import React from "react";
import { View, StyleSheet, Image, Platform } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import moment from "moment";
import { toAccountBalanceString, toHumanDollars } from "../../../utils/money";
import {
  DomusoProperty,
  DomusoPropertyProfile,
  DomusoPropertyFeeProfile,
} from "../../../../types/db-types";
import {
  typography,
  theme,
  OnScreenAlert,
  Text,
  NavigationButton,
  Icon,
  Tooltip,
} from "ui-components";
import {
  deriveUnitTitle,
  getPropertyAddressFromPropertyProfile,
} from "../../../screens/new/utils/helper";
import { translate } from "../../../i18n/translate";
import automationTestingLocators from ".././../../screens/new/utils/automation-testing-locators.json";
import { usePaymentMethodInformation } from "../../../hooks/usePaymentMethodInformation";

interface SingleReservation {
  accounting_details: any;
  account_system_type: any;
  property_unit: string;
  prop_name: string;
  prop_id: string;
  balance_due: string;
  allowed_payments: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  accounting_id: string;
  yardi_id: string;
  id: string;
  status: string;
  is_blacklisted: boolean;
  is_blocked: boolean;
  isPaymentEnabled: boolean;
}
export interface ActiveAutoPay {
  schedule_date: any;
  next_due_date: any;
  id: any;
  reservation_id: any;
}
interface ReservationCardProps extends SingleReservation {
  onPress: any;
  navigation: any;
  service: any;
  property: DomusoProperty;
  account_number: string;
  property_profile: DomusoPropertyProfile;
  property_fee_profile: DomusoPropertyFeeProfile;
  active_auto_pay: ActiveAutoPay;
  homepageAction: any;
}
export const ReservationCard = (cardProps: ReservationCardProps) => {
  const {
    accounting_details = {},
    account_system_type,
    accounting_id,
    account_number,
    active_auto_pay,
    allowed_payments,
    balance_due,
    id,
    is_blocked,
    navigation,
    prop_id,
    is_blacklisted,
    prop_name,
    property,
    property_fee_profile,
    property_profile,
    property_unit,
    isPaymentEnabled,
    status,
    homepageAction,
  } = cardProps;

  const {
    paymentSources,
    isLoading: paymentMethodsLoading,
    error: paymentSourcesError,
  } = usePaymentMethodInformation({
    keepInactive: false,
    updated: false,
    allowACH: true,
    allowCC: true,
  });

  let balanceDue = isNaN(accounting_details.balanceDue)
    ? accounting_details.balanceDue
    : toAccountBalanceString(accounting_details.balanceDue);
  const styles = StyleSheet.create({
    cardContainer: {
      width: "100%",
      backgroundColor: theme.colors.white,
      borderRadius: 10,
      borderColor: theme.colors.stroke,
      borderWidth: 1,
      padding: 12,
    },
    propNameStyle: {
      ...typography.heading2,
      fontWeight: "500",
      color: theme.colors.secondary,
      marginRight: 4,
      lineHeight: 24,
    },
    propUnitStyle: {
      ...typography.title1,
      fontFamily: "Lato_400Regular",
      fontWeight: "500",
      color: theme.colors.text3,
    },
    propAddressStyle: {
      ...typography.subHeading,
      fontFamily: "Lato_700Bold",
      color: theme.colors.text2,
      flex: 1,
      marginTop: 2,
    },
    accountBalanceDescriptionStyle: {
      ...typography.title2,
      color: theme.colors.text2,
      flex: 1,
    },
    accountBalanceTextStyle: {
      ...typography.heading2,
      fontWeight: "500",
      color: theme.colors.secondary,
    },
    accountBalanceDiscountOnRent: {
      ...typography.body,
      fontWeight: "500",
      color: theme.colors.text3,
    },
    accountBalanceTooltipStyle: {
      marginTop: 7,
      marginLeft: 10,
    },
    autoPayTitleStyle: {
      ...typography.heading2,
      fontSize: 22,
      fontWeight: "500",
      color: theme.colors.secondary,
    },
    autoPayStatusStyle: {
      ...typography.subHeading,
      fontWeight: "500",
      marginLeft: 12,
      marginTop: 5,
      flex: 1,
    },
    enabledText: {
      color: theme.colors.success,
    },
    disabledText: {
      color: theme.colors.darkGrey,
    },
    manageAutoPayTextStyle: {
      ...typography.title2,
      color: theme.colors.primary,
      textAlign: "right",
      marginTop: 4,
      fontWeight: "400",
      flex: 1,
      lineHeight: 32,
    },
    scheduleText: {
      ...typography.body,
      color: theme.colors.text3,
    },
    noReservationsContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: 10,
    },
    instructionImageContainer: {
      borderWidth: 1,
      padding: 8,
      borderRadius: 10,
      borderColor: theme.colors.stroke,
      height: 64,
      width: 64,
    },
  });

  const getAutoPayPaymentDeviceInfo = (reservationInfo: any, paymentSources: any) => {
    const payload = { name: "loading..." };

    paymentSources?.forEach((item: any) => {
      if (reservationInfo?.payment_source_id === item?.id) {
        if (item?.nickname) {
          payload.name = item.nickname;
        } else {
          if (item?.bank_account_type) {
            payload.name = `${item.bank_account_type} ...xxxx ${item.last_four_digits}`;
          } else if (item?.card_type) {
            payload.name = `${item.card_type} ...xxxx ${item.last_four_digits}`;
          } else {
            payload.name = "payment method unknown";
          }
        }
      }
    });

    return payload;
  };

  const autoPayPaymentDeviceInfo = getAutoPayPaymentDeviceInfo(
    active_auto_pay,
    paymentSources
  )?.name;

  const renderAccountBalance = () => {
    if (
      !homepageAction?.showAccountBalance?.visible ||
      homepageAction?.hasBalanceError ||
      homepageAction?.hasError ||
      homepageAction?.missingAllowedPayments
    ) {
      return <></>;
    } else {
      return (
        <View style={{ marginTop: 20 }}>
          <View style={{ flex: 1, flexDirection: "row" }}>
            <View>
              <Text
                style={styles.accountBalanceDescriptionStyle}
                {...automationTestingLocators.reservationCard.accountBalanceTitle}>
                {translate("homepage.reservationCard.accountBalance")}
              </Text>
            </View>
          </View>
          <View style={{ flexDirection: "row" }}>
            <View
              style={{
                flexDirection: "row",
              }}>
              <Text
                style={styles.accountBalanceTextStyle}
                {...automationTestingLocators.reservationCard.accountBalance}>
                {homepageAction.isAimPastResident
                  ? translate("paymentFlow.details.contactPropertyForBalance")
                  : balanceDue}
              </Text>
              <View
                style={[
                  styles.accountBalanceTooltipStyle,
                  {
                    display: balanceDue.includes("(") ? "flex" : "none",
                  },
                ]}>
                <Tooltip
                  title={translate("homepage.reservationCard.negativeBalanceTitle")}
                  body={translate("homepage.reservationCard.negativeBalanceBody")}
                  width={"70%"}
                  widthWeb={325}
                  {...automationTestingLocators.reservationCard.negativeBalanceTooltip}>
                  <Icon name={"error"} size={theme.iconSize.medium} color={theme.colors.warning} />
                </Tooltip>
              </View>
            </View>
          </View>
          {homepageAction?.showAccountBalanceDiscountOnRent?.visible && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
              }}>
              <Text
                style={styles.accountBalanceDiscountOnRent}
                {...automationTestingLocators.reservationCard.accountBalanceDiscountOnRent}>
                {translate("homepage.reservationCard.accountBalanceDiscountOnBalance1")}?
              </Text>
              <View
                style={{ marginLeft: 5, alignItems: "center" }}
                {...automationTestingLocators.reservationCard
                  .accountBalanceDiscountOnRentTooltipContainer}>
                <Tooltip
                  showHeader={false}
                  body={
                    translate(
                      "homepage.reservationCard.accountBalanceDiscountOnBalanceTooltipBody"
                    ) as string
                  }
                  width={"70%"}
                  widthWeb={325}
                  containerStyle={{ padding: 10 }}>
                  <Icon name={"info"} size={16} color={theme.colors.warning} />
                </Tooltip>
              </View>
            </View>
          )}
        </View>
      );
    }
  };

  const navigateToRewardsPage = () => {
    navigation.navigate("Actions", {
      screen: "Rewards",
    });
  };
  const navigateToPaymentHistory = () => {
    navigation.navigate("SpecificReservation", {
      account_number,
      accountingId: accounting_id,
      accountSystemType: account_system_type,
      accountingDetails: accounting_details,
      allowedPayments: allowed_payments,
      balanceDue: balance_due,
      propertyUnit: property_unit,
      propName: prop_name,
      reservationId: id,
      currentProperty: property,
      currentPropertyProfile: property_profile,
      currentPropertyFeeProfile: property_fee_profile,
      activeAutoPay: active_auto_pay,
      isBlacklisted: is_blacklisted,
      isBlocked: is_blocked,
      hasEnabledPaymentMethods: isPaymentEnabled,
      paymentHistorySelected: true,
      isAimPastResident: homepageAction.isAimPastResident,
    });
  };

  const navigateToBalanceDetails = () => {
    navigation.navigate("SpecificReservation", {
      account_number,
      accountingId: accounting_id,
      accountSystemType: account_system_type,
      accountingDetails: accounting_details,
      allowedPayments: allowed_payments,
      balanceDue: balance_due,
      propertyUnit: property_unit,
      propName: prop_name,
      reservationId: id,
      currentProperty: property,
      currentPropertyProfile: property_profile,
      currentPropertyFeeProfile: property_fee_profile,
      activeAutoPay: active_auto_pay,
      isBlacklisted: is_blacklisted,
      isBlocked: is_blocked,
      hasEnabledPaymentMethods: isPaymentEnabled,
      paymentHistorySelected: false,
    });
  };

  const navigateToAutopay = () => {
    if (active_auto_pay) {
      return navigation.navigate("HomepageAutopayManage", {
        ...cardProps,
        accountingDetails: accounting_details,
        balanceDue: balance_due,
        propertyUnit: property_unit,
        reservationId: id,
        activeAutoPay: active_auto_pay,
        currentProperty: property,
        currentPropertyProfile: property_profile,
        currentPropertyFeeProfile: property_fee_profile,
        autoPayPaymentDeviceInfo,
      });
    } else {
      return navigation.navigate("Actions", {
        screen: "PaymentAutoPayDetails",
        params: {
          ...cardProps,
          accountingDetails: accounting_details,
          balanceDue: balance_due,
          propertyUnit: property_unit,
          reservationId: id,
          activeAutoPay: active_auto_pay,
          currentProperty: property,
          currentPropertyProfile: property_profile,
          currentPropertyFeeProfile: property_fee_profile,
          propName: prop_name,
        },
      });
    }
  };

  const navigateToMaintenanceRequests = () => {
    navigation.navigate("MaintenanceRequests", {
      ...cardProps,
      account_number,
      accountingDetails: accounting_details,
      allowedPayments: allowed_payments,
      balanceDue: balance_due,
      propertyUnit: property_unit,
      propName: prop_name,
      reservationId: id,
      currentProperty: property,
      currentPropertyProfile: property_profile,
      currentPropertyFeeProfile: property_fee_profile,
      activeAutoPay: active_auto_pay,
      isBlacklisted: is_blacklisted,
      isBlocked: is_blocked,
    });
  };

  if (homepageAction?.reservations && homepageAction?.reservations?.length) {
    return (
      <View style={{ width: "100%", flex: 1 }}>
        {homepageAction?.isBlacklist ? (
          <>
            <OnScreenAlert
              title={translate("homepage.reservationCard.blacklistedHomePageTitle") as string}
              alertType="alert"
              body={translate("homepage.reservationCard.blacklistedHomePageText") as string}
              {...automationTestingLocators.reservationCard.blacklistedOnScreenAlert}
            />
            <View style={{ marginTop: 15 }} />
          </>
        ) : null}
        <View style={styles.cardContainer}>
          <View>
            <View style={{ flexDirection: "row", alignItems: "flex-start", flex: 1 }}>
              <Text style={{ flex: 1 }}>
                <Text
                  style={styles.propNameStyle}
                  {...automationTestingLocators.reservationCard.propertyName}>
                  {`${prop_name}`}
                </Text>
                <Text
                  style={styles.propUnitStyle}
                  {...automationTestingLocators.reservationCard.propertyUnit}>
                  {`  (${deriveUnitTitle(property_unit)})`}
                </Text>
              </Text>
            </View>
            <View>
              <Text
                style={styles.propAddressStyle}
                {...automationTestingLocators.reservationCard.propertyAddress}>
                {getPropertyAddressFromPropertyProfile(property_profile)}
              </Text>
            </View>

            <View>
              {!["string", "number"].includes(typeof balanceDue) ? (
                <View
                  style={{
                    flex: 1,
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    marginTop: 20,
                  }}>
                  <View style={{ margin: 16 }} />
                  <ActivityIndicator
                    animating={true}
                    color={theme.colors.error}
                    {...automationTestingLocators.reservationCard.accountBalanceLoader}
                  />
                </View>
              ) : (
                renderAccountBalance()
              )}
            </View>

            {homepageAction?.showAutopayContent?.visible && (
              <View style={{ marginTop: 18 }}>
                <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                  <View style={{ flexDirection: "row", flex: 1, alignItems: "center" }}>
                    <Text
                      style={styles.autoPayTitleStyle}
                      {...automationTestingLocators.reservationCard.autopayTitle}>
                      {translate("homepage.reservationCard.autoPay")}
                    </Text>
                    <Text
                      style={[
                        styles.autoPayStatusStyle,
                        active_auto_pay ? styles.enabledText : styles.disabledText,
                      ]}
                      {...automationTestingLocators.reservationCard.autopayStatus}>
                      {`${
                        active_auto_pay
                          ? translate("homepage.reservationCard.enabled")
                          : translate("homepage.reservationCard.disabled")
                      }`}
                    </Text>
                  </View>
                  <Text
                    style={styles.manageAutoPayTextStyle}
                    suppressHighlighting={true}
                    onPress={navigateToAutopay}
                    {...automationTestingLocators.reservationCard.manageAutoPayButton}>
                    {translate("homepage.reservationCard.manageAutoPay")}
                  </Text>
                </View>

                {active_auto_pay && (
                  <View>
                    <Text
                      style={styles.scheduleText}
                      {...automationTestingLocators.reservationCard.autopayScheduleDateText}>
                      {`${translate("homepage.reservationCard.scheduledFor")} ${moment(
                        active_auto_pay?.next_due_date,
                        "MM/DD/YYYY"
                      ).format("MMMM DD, YYYY")} with `}
                      <Text
                        style={{ color: theme.colors.secondary }}
                        {...automationTestingLocators.reservationCard.autopaySchedulePaymentMethod}>
                        {`${autoPayPaymentDeviceInfo}`}
                      </Text>
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>
        </View>

        <View>
          {homepageAction?.rewardsButton?.visible && (
            <>
              <View style={{ marginTop: 20 }} />
              <NavigationButton
                text={translate("homepage.reservationCard.rewards") as string}
                disabled={homepageAction?.rewardsButton?.disabled?.length > 0}
                onPress={navigateToRewardsPage}
                type="primary"
                showIcon
                iconName="dollar-circle"
                iconsSize={24}
                {...automationTestingLocators.reservationCard.rewardsButton}
              />
            </>
          )}
          {homepageAction?.paymentHistoryButton?.visible && (
            <>
              <View style={{ marginTop: 15 }} />
              <NavigationButton
                text={translate("homepage.reservationCard.viewPaymentHistory") as string}
                onPress={navigateToPaymentHistory}
                disabled={homepageAction?.paymentHistoryButton?.disabled?.length > 0}
                {...automationTestingLocators.reservationCard.viewPaymentHistoryButton}
              />
            </>
          )}
          {homepageAction?.balanceDetailsButton?.visible && (
            <>
              <View style={{ marginTop: 15 }} />
              <NavigationButton
                text={translate("homepage.reservationCard.viewBalanceDetails") as string}
                disabled={homepageAction?.balanceDetailsButton?.disabled?.length > 0}
                onPress={navigateToBalanceDetails}
                {...automationTestingLocators.reservationCard.viewBalanceDetailsButton}
              />
            </>
          )}
          {homepageAction?.maintenanceRequestsButton?.visible && (
            <>
              <View style={{ marginTop: 15 }} />
              <NavigationButton
                text={translate("homepage.reservationCard.viewMaintenanceRequests") as string}
                disabled={homepageAction?.maintenanceRequestsButton?.disabled?.length > 0}
                onPress={navigateToMaintenanceRequests}
                {...automationTestingLocators.reservationCard.viewMaintenanceRequestsButton}
              />
            </>
          )}
        </View>
      </View>
    );
  } else if (homepageAction?.hasError) {
    if (homepageAction?.reservations && homepageAction?.reservations.length > 0) {
      return (
        <OnScreenAlert
          title={translate("homepage.reservationCard.reservationErrorTitle") as string}
          alertType="alert"
          body={translate("homepage.reservationCard.reservationErrorMessage") as string}
          {...automationTestingLocators.reservationCard.reservationErrorOnScreenAlert}
        />
      );
    } else {
      return (
        <OnScreenAlert
          title={translate("homepage.reservationCard.reservationsMissingTitle") as string}
          alertType="alert"
          body={translate("homepage.reservationCard.reservationsMissingMessage") as string}
          {...automationTestingLocators.reservationCard.reservationsMissingOnScreenAlert}
        />
      );
    }
  } else {
    return homepageAction?.isCustomer ? (
      <View style={styles.noReservationsContainer}>
        <OnScreenAlert
          title={translate("homepage.reservationCard.noReservationsTitle") as string}
          alertType="caution"
          body={translate("homepage.reservationCard.noReservationsParagraph") as string}
          renderImage={
            <View style={styles.instructionImageContainer}>
              <Image
                source={require("../../../../assets/property.png")}
                style={{ height: 45, width: 45 }}
              />
            </View>
          }
          {...automationTestingLocators.reservationCard.noReservationsOnScreenAlert}
        />
      </View>
    ) : (
      <OnScreenAlert
        title={translate("homepage.reservationCard.managerNoticeTitle") as string}
        alertType="alert"
        body={translate("homepage.reservationCard.managerNoticeMessage") as string}
        {...automationTestingLocators.reservationCard.managerNoticeOnScreenAlert}
      />
    );
  }
};
